/**
 * UI-8d Task 5 — usePlatformAuditLog hook.
 *
 * Read-only paginated fetcher over GET `/platform/audit-log`
 * (apps/api/src/routes/platform/audit-log/list.ts). Mirrors
 * `useAuditLog` (UI-7a) in shape and behavior: same `AuditEntry` row
 * type, same lazy-pagination cursor, same `AuditFilters` filter object
 * — only the endpoint differs.
 *
 * The platform audit log is the system-wide chain (actors are platform
 * principals like `system:cron`, no tenantId scoping); the tenant log
 * lives at `/admin/compliance/audit-log`. Both backends return the same
 * envelope: `{ rows, total, limit, offset }`.
 *
 * Pagination contract:
 *   - `limit` fixed at 50/page (backend caps at 200; the UI sticks to 50
 *     to match useAuditLog and the virtualized DataTable's itemSize).
 *   - `loadNextPage()` increments the offset by 50 and APPENDS to `rows`
 *     so the virtualizer can scroll smoothly without losing state.
 *   - Filter changes reset offset to 0 and replace `rows`.
 *
 * Re-exports `AuditEntry` and `AuditFilters` from the UI-7a hook so the
 * PlatformAuditLogPage doesn't end up with two parallel type imports.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import type { AuditEntry, AuditFilters } from './useAuditLog.js'

export type { AuditEntry, AuditFilters } from './useAuditLog.js'

export interface UsePlatformAuditLogResult {
  rows: AuditEntry[]
  total: number
  loading: boolean
  error: string | null
  loadNextPage: () => Promise<void>
  refetch: () => Promise<void>
}

interface AuditLogResponse {
  rows: AuditEntry[]
  total: number
  limit: number
  offset: number
}

const PAGE_SIZE = 50

function buildPath(filters: AuditFilters, offset: number): string {
  const params = new URLSearchParams()
  if (filters.actorId) params.set('actorId', filters.actorId)
  if (filters.action) params.set('action', filters.action)
  if (filters.resourceType) params.set('resourceType', filters.resourceType)
  if (filters.from) params.set('from', filters.from.toISOString())
  if (filters.to) params.set('to', filters.to.toISOString())
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return `/platform/audit-log?${params.toString()}`
}

export function usePlatformAuditLog(
  filters: AuditFilters,
): UsePlatformAuditLogResult {
  const [rows, setRows] = useState<AuditEntry[]>([])
  const [total, setTotal] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Serialize filter values to primitives so the effect deps don't churn
  // on every render when callers pass a fresh `{}` object literal.
  const actorKey = filters.actorId ?? ''
  const actionKey = filters.action ?? ''
  const resourceTypeKey = filters.resourceType ?? ''
  const fromKey = filters.from ? filters.from.toISOString() : ''
  const toKey = filters.to ? filters.to.toISOString() : ''

  // Mounted guard prevents setState after unmount.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Latest-filters ref so callbacks (refetch / loadNextPage) always see the
  // current filter shape without being recreated on every keystroke.
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  // Where the next loadNextPage() should start. Reset to 0 on every filter
  // change (see effect below).
  const offsetRef = useRef(0)

  const fetchPage = useCallback(
    async (offset: number): Promise<AuditLogResponse> => {
      return api.get<AuditLogResponse>(buildPath(filtersRef.current, offset))
    },
    [],
  )

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const resp = await fetchPage(0)
      if (mountedRef.current) {
        setRows(resp.rows)
        setTotal(resp.total)
        // Advance the page cursor by PAGE_SIZE (not by resp.rows.length) so
        // partial final pages don't shorten the stride.
        offsetRef.current = PAGE_SIZE
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(
          e instanceof Error ? e.message : 'Failed to load platform audit log',
        )
        setRows([])
        setTotal(0)
      }
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [fetchPage])

  const loadNextPage = useCallback(async (): Promise<void> => {
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      const resp = await fetchPage(offsetRef.current)
      if (mountedRef.current) {
        setRows((prev) => [...prev, ...resp.rows])
        setTotal(resp.total)
        offsetRef.current += PAGE_SIZE
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(
          e instanceof Error ? e.message : 'Failed to load platform audit log',
        )
      }
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [fetchPage, loading])

  // Reset + refetch whenever any filter primitive changes.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    offsetRef.current = 0
    api
      .get<AuditLogResponse>(buildPath(filtersRef.current, 0))
      .then((resp) => {
        if (cancelled) return
        setRows(resp.rows)
        setTotal(resp.total)
        offsetRef.current = PAGE_SIZE
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load platform audit log',
        )
        setRows([])
        setTotal(0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return (): void => {
      cancelled = true
    }
  }, [actorKey, actionKey, resourceTypeKey, fromKey, toKey])

  return { rows, total, loading, error, loadNextPage, refetch }
}
